import React, {
  useMemo,
  ReactNode,
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";

import { useAppRouteParams } from "@/AppRoutes";
import {
  getAPIErrorDetailAndMessage,
  getAPIErrorDetailOrMessage,
} from "@/api/helpers";
import {
  getVersionCategories,
  getVersionCategory,
} from "@/api/versionCategory";
import { useAppNotificationManager } from "@/components/AppProvider/AppNotificationProvider";
import { VersionCategoryRaw } from "@/domain/VersionCategories";
import { translation } from "@/i18n";
import { nsVersion } from "@/i18n/locales/version";
import { usePermissions } from "../PermissionsProvider";

const useTranslation = translation.use(nsVersion);

const versionCategoriesCtx = createContext<{
  versionCategories: VersionCategoryRaw[] | null;
  setVersionCategories: React.Dispatch<
    React.SetStateAction<VersionCategoryRaw[] | null>
  >;
  categoriesDropdown: { value: string; text: string }[];
  isLoadedVersionCategories: boolean;
  runReloadVersionCategories: () => Promise<void>;
}>({
  versionCategories: [],
  setVersionCategories: () => {},
  categoriesDropdown: [],
  isLoadedVersionCategories: false,
  runReloadVersionCategories: async () => {},
});

// 組織ごとのカテゴリを提供するプロバイダ
// version_categories:readとversions_version_category:read:idの
// ２つの権限を見るためuseAPIを使用しない
export const VersionCategoriesProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { t } = useTranslation();
  const { organizationId } = useAppRouteParams();
  const { me, hasPermission } = usePermissions();
  const { showErrorNotification } = useAppNotificationManager();
  const hasVersionCategoriesReadPermission = hasPermission(
    "version_categories",
    "read"
  );
  const [_, setError] = useState<unknown>(null);

  const [versionCategories, setVersionCategories] = useState<
    VersionCategoryRaw[] | null
  >(null);
  const [isLoadedVersionCategories, setIsLoadedVersionCategories] =
    useState<boolean>(false);
  const runReloadVersionCategories = useCallback(async () => {
    if (!organizationId) return;
    try {
      setIsLoadedVersionCategories(false);
      if (hasVersionCategoriesReadPermission) {
        const response = await getVersionCategories({ organizationId });
        setVersionCategories(
          [...response].sort((a, b) => Math.sign(a.sort - b.sort))
        );
      } else {
        const versionCategoryIds = me?.permissions.version_category.read;
        if (!versionCategoryIds?.length) {
          setVersionCategories(null);
          setIsLoadedVersionCategories(true);
          return;
        }
        const settledResponses = await Promise.allSettled(
          versionCategoryIds.map((versionCategoryId) =>
            getVersionCategory({
              versionCategoryId,
              organizationId,
            })
          )
        );
        const response = settledResponses.map((result) => {
          if (result.status === "fulfilled") {
            return result.value;
          }
          const { httpStatus } = getAPIErrorDetailOrMessage(result.reason);
          if (httpStatus !== 404) {
            showErrorNotification(
              "カテゴリー情報の取得中にエラーが発生しました",
              result.reason
            );
          }
          return null;
        });
        setVersionCategories(
          [...response]
            .filter(
              (category): category is VersionCategoryRaw => category !== null
            )
            .sort((a, b) => Math.sign(a.sort - b.sort))
        );
      }
    } catch (err) {
      setError(() => {
        if (typeof err == "object") {
          const e = {
            ...err,
            message: getAPIErrorDetailAndMessage(err),
          } as Error;
          throw e;
        } else {
          throw new Error("不明なエラー");
        }
      });
    } finally {
      setIsLoadedVersionCategories(true);
    }
  }, [
    hasVersionCategoriesReadPermission,
    me,
    organizationId,
    showErrorNotification,
  ]);

  useEffect(() => {
    runReloadVersionCategories();
  }, [runReloadVersionCategories]);

  const categoriesDropdown: { value: string; text: string }[] = useMemo(
    () => [
      {
        value: "all",
        text: t.version.category.filterPlaceholder(),
      },
      ...(versionCategories?.map(({ id, name }) => ({
        value: id,
        text: name,
      })) ?? []),
      ...(hasVersionCategoriesReadPermission
        ? [
            {
              value: "",
              text: t.version.category.uncategorized(),
            },
          ]
        : []),
    ],
    [t, versionCategories, hasVersionCategoriesReadPermission]
  );

  const versionCategoriesCtxValue = useMemo(
    () => ({
      versionCategories,
      setVersionCategories,
      categoriesDropdown,
      runReloadVersionCategories,
      isLoadedVersionCategories,
    }),
    [
      versionCategories,
      categoriesDropdown,
      setVersionCategories,
      runReloadVersionCategories,
      isLoadedVersionCategories,
    ]
  );

  return (
    <versionCategoriesCtx.Provider value={versionCategoriesCtxValue}>
      {children}
    </versionCategoriesCtx.Provider>
  );
};

export const useVersionCategories = () => {
  return useContext(versionCategoriesCtx);
};
